import { EMessageType } from '../data/Constants';
import { t } from 'i18next';
import { toast, ToastOptions } from 'react-toastify';
import { isValidElement, ReactElement } from 'react';

import type { IToastUpdate } from '../../types';

/**
 * Shows a toast message using react-toastify.
 * @param {EMessageType} type - The type of message (Error, Success, Info, etc.).
 * @param {string | ReactElement} message - The message to display.
 * @param {boolean} [showDefault=true] - Whether to show the default message prefix.
 * @param {boolean} [autoClose=true] - Whether the toast should auto-close.
 */
export function showToastMessage(
  type: EMessageType,
  message: string | ReactElement,
  showDefault: boolean = true,
  autoClose: boolean = true
): void {
  switch (type) {
    case EMessageType.Error:
      handleError(message, showDefault, autoClose);
      break;
    case EMessageType.Success:
      handleSuccess(message, showDefault);
      break;
    case EMessageType.Info:
      toast.info(message);
      break;
    default:
      toast(message);
  }
}

/**
 * Shows an error toast message.
 * @param {string | ReactElement} message - The error message to display.
 * @param {boolean} [showDefault=true] - Whether to show the default error prefix.
 * @param {boolean} [autoClose=true] - Whether the toast should auto-close.
 */
export function handleError(
  message: string | ReactElement,
  showDefault: boolean = true,
  autoClose: boolean = true
): void {
  let errorMessage;

  // If message is a NOT ReactElement, add additional information to the string
  if (!isValidElement(message)) {
    errorMessage = message ? t('message.error.general') + message : t('message.error.default');
  }

  // If showDefault is false, use the provided message directly
  // This allows for custom error messages to be displayed without the default prefix
  if (!showDefault) {
    errorMessage = message;
  }

  const toastOptions: ToastOptions = {
    position: 'top-center',
    autoClose: autoClose ? 5000 : false,
    closeOnClick: false,
    theme: 'colored',
    draggable: true,
  };

  toast.error(errorMessage, toastOptions);
}

/**
 * Shows a success toast message.
 * @param {string | ReactElement} message - The success message to display.
 * @param {boolean} [showDefault=true] - Whether to show the default success prefix.
 */
export function handleSuccess(message: string | ReactElement, showDefault: boolean = true): void {
  let successMessage;

  // If message is a NOT ReactElement, add additional information to the string
  if (!isValidElement(message)) {
    successMessage = message
      ? t('message.success.general') + message
      : t('message.success.default');
  }

  // If showDefault is false, use the provided message directly
  // This allows for custom error messages to be displayed without the default prefix
  if (!showDefault) {
    successMessage = message;
  }

  const toastOptions: ToastOptions = {
    position: 'top-center',
    autoClose: 5000,
    closeOnClick: false,
    theme: 'colored',
    draggable: true,
  };
  toast.success(successMessage, toastOptions);
}

/**
 * Initiates a toast and returns its ID.
 * @param {string} message - The message to display in the toast.
 * @returns {string} The toast ID.
 */
export function initiateToast(message: string): string {
  const id = toast.info(message, {
    position: 'top-right',
    autoClose: false,
  });
  return id.toString();
}
/**
 * Updates an existing toast message by toastId.
 * @param {IToastUpdate} toastUpdateProps - The properties for updating the toast.
 */
export function updateToast(toastUpdateProps: IToastUpdate): void {
  const { toastId, type, message, className, autoClose } = toastUpdateProps;

  toast.update(toastId, {
    render: message,
    type: type,
    isLoading: false,
    className: className,
    autoClose: autoClose,
    position: 'top-right',
  });
}

/**
 * Returns the class name for a given toast type.
 * @param {EMessageType} type - The type of message.
 * @returns {string} The class name for the toast.
 */
export function getToastClassName(type: EMessageType): string {
  switch (type) {
    case EMessageType.Success:
      return 'toast-content-success';
    case EMessageType.Error:
      return 'toast-content-error';
    case EMessageType.Warning:
      return 'toast-content-warning';
    default:
      return 'toast-content-default';
  }
}
